import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { S25PipesModule } from "../../pipes/s25.pipes.module";
import { S25ButtonComponent } from "../s25.button.component";
import { S25IconButtonComponent } from "../s25.icon.button.component";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25FilestoreImageComponent } from "../s25.filestore.image.component";
import { NewsArticle, NewsListView } from "../../services/news.article.service";
import { S25RouterLinkDirective } from "../../modules/s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../services/state/state.map";
import { Item } from "../../pojo/Item";
import { DataAccess } from "../../dataaccess/data.access";
import { S25Util } from "../../util/s25-util";

type PublishScheduleStatus = "scheduled" | "archived";

@Component({
    selector: "s25-ng-news-article-list",
    template: `
        @if (!articles().length) {
            <div class="news-empty" role="status">
                <p>{{ emptyMessage() }}</p>
            </div>
        } @else {
            <div class="news-list">
                @for (article of articles(); track article.newsArticleId + (article.lastModDt ?? "")) {
                    <article class="news-card" [attr.aria-labelledby]="'article-title-' + article.newsArticleId">
                        <div class="news-card__meta">
                            @if (article.promotedToHome) {
                                <span class="promoted-badge">Promoted</span>
                            }
                            @if (canManage()) {
                                @let scheduleStatus = publishScheduleStatus(article);
                                @if (scheduleStatus === "scheduled") {
                                    <span class="schedule-badge schedule-badge--scheduled">Scheduled</span>
                                } @else if (scheduleStatus === "archived") {
                                    <span class="schedule-badge schedule-badge--archived">Archived</span>
                                }
                            }
                            @if (article.acctName && article.acctId) {
                                <a
                                    class="news-org-link"
                                    s25-ng-router-link
                                    [stateName]="orgRoutingStateName"
                                    [stateParams]="{ itemId: article.acctId }"
                                    [attr.aria-label]="'View ' + article.acctName + ' organization'"
                                >
                                    @if (!failedOrgLogoAcctIds().has(article.acctId)) {
                                        <img
                                            class="news-org-logo"
                                            [src]="orgLogoSrc(article.acctId)"
                                            [alt]="article.acctName + ' logo'"
                                            (error)="onOrgLogoError(article.acctId)"
                                        />
                                    }
                                    <span class="news-org-name">{{ article.acctName }}</span>
                                </a>
                            }
                            @if (article.createDt) {
                                <span class="news-date">
                                    <s25-ng-icon
                                        type="calendarCalendar"
                                        size="0.8em"
                                        aria-hidden="true"
                                        [ariaLabel]="false"
                                    />
                                    <time [attr.datetime]="article.createDt">
                                        {{ article.createDt | date: "MMM d, y" }}
                                    </time>
                                </span>
                            }
                            @if (canManage() && (article.publishStartDt || article.publishEndDt)) {
                                <span class="news-date">
                                    Publish:
                                    @if (article.publishStartDt) {
                                        <time [attr.datetime]="article.publishStartDt">
                                            {{ article.publishStartDt | date: "MMM d, y" }}
                                        </time>
                                    } @else {
                                        <span>Open</span>
                                    }
                                    –
                                    @if (article.publishEndDt) {
                                        <time [attr.datetime]="article.publishEndDt">
                                            {{ article.publishEndDt | date: "MMM d, y" }}
                                        </time>
                                    } @else {
                                        <span>Open</span>
                                    }
                                </span>
                            }
                        </div>

                        <div
                            class="news-card__main"
                            [class.news-card__main--has-image]="
                                !!article.imageUrl && !isExpanded(article.newsArticleId!)
                            "
                        >
                            @let expandable = needsExpand(article);
                            @let excerpt = plainTextExcerpt(article.description);
                            @if (article.imageUrl && !isExpanded(article.newsArticleId!)) {
                                <div
                                    class="news-card__image-pane"
                                    [class.news-card__image-pane--short]="!expandable"
                                    aria-hidden="true"
                                >
                                    <s25-ng-filestore-image [value]="article.imageUrl" [readOnly]="true" />
                                </div>
                            }

                            <div class="news-card__content">
                                <div class="news-card__header">
                                    <h3 [id]="'article-title-' + article.newsArticleId">
                                        {{ article.title }}
                                    </h3>
                                    <div
                                        class="news-card__actions"
                                        role="group"
                                        [attr.aria-label]="'Actions for ' + article.title"
                                    >
                                        @if (canManage()) {
                                            <s25-ng-icon-button
                                                [type]="'pencil'"
                                                [size]="'14px'"
                                                [ariaLabel]="'Edit ' + article.title"
                                                (clicked)="editArticle.emit(article)"
                                            />
                                            @if (deletingArticleId() === article.newsArticleId) {
                                                <s25-ng-button type="danger" (clicked)="onConfirmDelete(article)">
                                                    Confirm delete
                                                </s25-ng-button>
                                                <s25-ng-button type="outline" (clicked)="deletingArticleId.set(null)">
                                                    Cancel
                                                </s25-ng-button>
                                            } @else {
                                                <s25-ng-icon-button
                                                    [type]="'trash2'"
                                                    [size]="'14px'"
                                                    [ariaLabel]="'Delete ' + article.title"
                                                    (clicked)="deletingArticleId.set(article.newsArticleId!)"
                                                />
                                            }
                                        }
                                    </div>
                                </div>

                                @if (article.imageUrl && isExpanded(article.newsArticleId!)) {
                                    <div class="news-card__image">
                                        <s25-ng-filestore-image [value]="article.imageUrl" [readOnly]="true" />
                                    </div>
                                }

                                @if (isExpanded(article.newsArticleId!)) {
                                    @if (article.description) {
                                        <div
                                            class="news-card__description"
                                            [innerHTML]="article.description | safeHTML"
                                        ></div>
                                    }
                                } @else if (excerpt) {
                                    <p class="news-card__description news-card__description--excerpt">
                                        {{ excerpt }}
                                    </p>
                                }

                                @if (expandable) {
                                    <div class="news-card__footer">
                                        <s25-ng-button type="link" (clicked)="toggleExpanded(article.newsArticleId!)">
                                            {{ isExpanded(article.newsArticleId!) ? "Collapse" : "Read full article" }}
                                            <s25-ng-icon
                                                [type]="isExpanded(article.newsArticleId!) ? 'arrowUp' : 'arrowRight'"
                                                size="0.75em"
                                                [ariaLabel]="false"
                                            />
                                        </s25-ng-button>
                                    </div>
                                }
                            </div>
                        </div>
                    </article>
                }
            </div>
        }
    `,
    styles: `
        .news-empty {
            color: var(--color--text-muted);
            padding: 2rem 0;
            text-align: center;
            font-style: italic;
        }

        .news-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .news-card {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            transition:
                box-shadow var(--transition-medium),
                transform var(--transition-medium),
                border-color var(--transition-medium);
        }

        .news-card:hover {
            border-color: var(--cn-blue);
            box-shadow: 0 4px 12px rgba(148, 163, 184, 0.2);
            transform: translateY(-2px);
        }

        .news-card__meta {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            padding: 1rem 1rem 0.75rem;
        }

        .news-card__main {
            display: flex;
            flex-direction: column;
            min-width: 0;
        }

        .news-card__main--has-image {
            flex-direction: row;
            align-items: flex-start;
        }

        .news-card__image-pane {
            flex: 0 0 200px;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding-left: 1rem;
        }

        .news-card__image-pane--short {
            padding: 0.75rem 0 1rem 1rem;
        }

        .news-card__image-pane s25-ng-filestore-image {
            display: flex;
            align-items: flex-start;
            justify-content: center;
            width: 100%;
        }

        .news-card__image-pane ::ng-deep .fs-image__preview {
            max-width: 200px;
            max-height: 200px;
            width: auto;
            height: auto;
            object-fit: contain;
        }

        .news-card__image ::ng-deep .fs-image__preview {
            max-width: 100%;
            max-height: 320px;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
            margin-bottom: 1rem;
        }

        .news-card__content {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-width: 0;
            padding: 0 1rem 1rem;
        }

        .news-card__main--has-image .news-card__content {
            padding-top: 0;
        }

        .news-date {
            display: inline-flex;
            align-items: center;
            gap: 0.3rem;
            font-size: 0.85rem;
            color: var(--color--text-muted);
        }

        .promoted-badge {
            background: rgba(var(--cn-blue-rgb), 0.12);
            color: var(--cn-blue);
            padding: 0.2rem 0.6rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
        }

        .schedule-badge {
            padding: 0.2rem 0.6rem;
            border-radius: 999px;
            font-size: 0.75rem;
            font-weight: 600;
        }

        .schedule-badge--scheduled {
            background: rgba(var(--cn-blue-rgb), 0.08);
            color: var(--color--text-muted);
        }

        .schedule-badge--archived {
            background: var(--color--surface-2-bg);
            color: var(--color--text-muted);
        }

        .news-org-link {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            text-decoration: none;
            color: inherit;
        }

        .news-org-logo {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            object-fit: cover;
            flex-shrink: 0;
        }

        .news-org-name {
            font-size: 0.85rem;
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .news-org-link:hover .news-org-name,
        .news-org-link:focus .news-org-name,
        .news-org-link:focus-visible .news-org-name {
            text-decoration: underline;
        }

        .news-card__header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 0.75rem;
        }

        .news-card__header h3 {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--color--text-strong);
        }

        .news-card__actions {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            flex-shrink: 0;
            line-height: 1;
        }

        .news-card__description {
            flex: 1;
            margin-bottom: 0.5rem;
        }

        .news-card__description--excerpt {
            margin: 0;
            line-height: 1.6;
        }

        .news-card__description ::ng-deep p {
            margin: 0;
        }

        .news-card__footer {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        @media (max-width: 768px) {
            .news-card__main--has-image {
                flex-direction: column;
            }

            .news-card__image-pane {
                flex: 0 0 auto;
                width: 100%;
                max-height: 200px;
                padding-left: 0;
            }

            .news-card__content {
                padding: 0 1rem 1rem;
            }

            .news-card__main--has-image .news-card__content {
                padding-top: 0.75rem;
            }

            .news-card__header {
                flex-direction: column;
                gap: 0.5rem;
            }

            .news-card__meta {
                font-size: 0.85rem;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        S25PipesModule,
        S25ButtonComponent,
        S25IconButtonComponent,
        S25IconComponent,
        S25FilestoreImageComponent,
        S25RouterLinkDirective,
    ],
})
export class NewsArticleListComponent {
    private static readonly COLLAPSED_EXCERPT_LENGTH = 200;

    articles = input.required<NewsArticle[]>();
    canManage = input(false);
    listView = input<NewsListView>("active");

    editArticle = output<NewsArticle>();
    deleteArticle = output<NewsArticle>();

    emptyMessage = computed(() => (this.listView() === "archived" ? "No archived articles." : "No news articles yet."));

    protected readonly orgRoutingStateName = S25States.names.seOrganizationDetails;

    readonly failedOrgLogoAcctIds = signal<Set<number>>(new Set());

    readonly deletingArticleId = signal<string | null>(null);
    readonly expandedArticleIds = signal<Set<string>>(new Set());

    orgLogoSrc(acctId: number): string {
        return DataAccess.getUrl(
            `/se/object/logo?${DataAccess.params({
                object_type: Item.Ids.Organization,
                object_id: acctId,
            })}`,
        );
    }

    onOrgLogoError(acctId: number): void {
        this.failedOrgLogoAcctIds.update((current) => new Set([...current, acctId]));
    }

    isExpanded(articleId: string): boolean {
        return this.expandedArticleIds().has(articleId);
    }

    needsExpand(article: NewsArticle): boolean {
        const articleId = article.newsArticleId;
        if (!articleId) {
            return false;
        }
        if (this.isExpanded(articleId)) {
            return true;
        }
        return this.plainText(article.description).length > NewsArticleListComponent.COLLAPSED_EXCERPT_LENGTH;
    }

    plainTextExcerpt(html: string | undefined): string {
        const text = this.plainText(html);
        if (!text) {
            return "";
        }
        if (text.length <= NewsArticleListComponent.COLLAPSED_EXCERPT_LENGTH) {
            return text;
        }
        return `${text.slice(0, NewsArticleListComponent.COLLAPSED_EXCERPT_LENGTH).trimEnd()}…`;
    }

    toggleExpanded(articleId: string): void {
        this.expandedArticleIds.update((ids) => {
            const next = new Set(ids);
            next.has(articleId) ? next.delete(articleId) : next.add(articleId);
            return next;
        });
    }

    onConfirmDelete(article: NewsArticle): void {
        this.deletingArticleId.set(null);
        this.deleteArticle.emit(article);
    }

    publishScheduleStatus(article: NewsArticle): PublishScheduleStatus | null {
        const now = new Date();
        const end = article.publishEndDt ? S25Util.date.parse(article.publishEndDt) : null;
        if (end && S25Util.date.isValid(end) && now.getTime() > end.getTime()) {
            return "archived";
        }
        const start = article.publishStartDt ? S25Util.date.parse(article.publishStartDt) : null;
        if (start && S25Util.date.isValid(start) && now.getTime() < start.getTime()) {
            return "scheduled";
        }
        return null;
    }

    plainText(html: string | undefined): string {
        if (!html) {
            return "";
        }
        const element = document.createElement("div");
        element.innerHTML = html;
        return (element.textContent ?? "")
            .replace(/\u00A0/g, " ")
            .replace(/\s+/g, " ")
            .trim();
    }
}
